import { caseActivityQueryOptions } from "@/features/case-evaluation/api/queries/acitivities-query";
import {
	caseEvidenceListAllQueryOptions,
	caseEvidenceQueryOptions,
	useEvidenceMultipleSubmarkingMutation,
	useEvidenceMutation,
	useEvidenceSubmarkingMutation,
} from "@/features/case-evaluation/api/queries/evidence-query";
import CaseEvidenceEditModal from "@/features/case-evaluation/components/case-evidence-edit-modal";
import CaseEvidenceUpdateAdmissionStatusModal from "@/features/case-evaluation/components/case-evidence-update-admission-status-modal";
import CaseEvidenceUpdateMarkingStatusModal from "@/features/case-evaluation/components/case-evidence-update-marking-status-modal";
import { type CaseEvidence, type CaseEvidenceForm, type EvidenceStatusForm, evidenceStatusFormSchema } from "@/features/case-evaluation/types/evidence";
import type { Media } from "@/features/dashboard/types/media.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { DocumentEditor } from "@onlyoffice/document-editor-react";
import {
	IconArrowLeft,
	IconArrowRight,
	IconChevronDown,
	IconDatabaseExclamation,
	IconDots,
	IconFile,
	IconPencil,
	IconTemplate,
	IconTrash,
} from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import clsx from "clsx";
import moment from "moment";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import ConfirmWithPasswordModal from "../confirm-with-password-modal";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../core/actions/dropdown-menu";
import { Blanket, BlanketImage, BlanketInfo } from "../core/blanket";
import { Button } from "../core/buttons/button";
import { Form } from "../core/forms/form";
import { useModal } from "../core/modals/use-modal";
import { useModalControl } from "../core/modals/use-modal-controller";
import { ScrollArea } from "../core/scroller";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../core/tabs";
import Tooltip from "../core/tooltip";
import { EvidencePdfViewer } from "../pdf-viewer/evidence-pdf-viewer";
import { FileViewer } from "./file-viewer";
import { ImageViewerWithAddEvidence } from "./image-viewer-with-add-evidence";

export function HearingEvidenceFileViewer({
	attachment,
	evidenceId,
	caseActivityId,
	caseId,
	index,
	options,
	token,
}: {
	attachment?: Media;
	evidenceId?: string;
	caseActivityId?: string;
	caseId?: string;
	index?: number;
	token?: string;
	options?: Record<string, string | number>;
}) {
	const modal = useModal();
	const [newEvidenceId, setNewEvidenceId] = useState<string | undefined>(evidenceId);
	const caseEvidenceMutation = useEvidenceMutation();
	const caseEvidenceSubmarkingMutation = useEvidenceSubmarkingMutation();

	const dataQuery = useSuspenseQuery(
		caseEvidenceListAllQueryOptions({
			id: newEvidenceId,
		}),
	);
	const data = dataQuery.data?.data[0];

	const activityQuery = useSuspenseQuery(
		caseActivityQueryOptions({
			id: caseId || data?.case_id,
			params: caseActivityId,
		}),
	);
	const activityData = activityQuery.data?.data;

	const subMarkingQuery = useSuspenseQuery(
		caseEvidenceQueryOptions({
			case_id: data?.case_id || caseId,
			parent_id: data?.id || evidenceId,
			evidence_type: data?.evidence_type,
			case_record_user_id: data?.case_record_user_id,
		}),
	);

	const submarkings = subMarkingQuery.data?.data;

	const evidenceImageUpdateMutation = useEvidenceMutation();
	const expungeCaseMutation = useEvidenceMutation();
	const multipleSubmarkingMutation = useEvidenceMultipleSubmarkingMutation(data?.case_id);

	const config = {
		...options,
		token,
	};

	const handleClick = (sub: CaseEvidence) => {
		const file = sub.evidence;

		const isValidFile = file.original_url;
		const attachment = isValidFile
			? sub.evidence
			: {
					name: file.name,
					size: file.size,
					mime_type: file.type,
					original_url: file.preview,
					preview_url: file.preview,
					updated_at: file.updated_at,
				};

		modal.openModal({
			title: "File Preview",
			size: "full",
			showHeader: false,
			showFooter: false,
			component: <FileViewer attachment={attachment} />,
		});
	};

	const form = useForm<EvidenceStatusForm>({
		resolver: zodResolver(evidenceStatusFormSchema),
		defaultValues: {
			id: "", //the value will be set on click of pencil icon
			parent_id: "", //the value will be set on click of pencil icon
			case_id: data?.case_id,
			evidence_type: data?.evidence_type,
			parties: data?.parties,
			case_record_user_id: data?.case_record_user_id || "",
			case_activity_id: caseActivityId || "",
		},
	});
	const storedTime = sessionStorage.getItem("password_entered_at");

	const handleSubmit = async (values: CaseEvidenceForm) => {
		values.case_activity_id = caseActivityId;
		const expirationTime = moment(storedTime).add(5, "minutes");
		if (storedTime && expirationTime !== undefined && moment().isBefore(expirationTime)) {
			await caseEvidenceMutation.mutateAsync(values);
		} else {
			try {
				modal.openModal({
					allowClose: true,
					title: "Confirm Submit",
					description: "Are you sure? This action cannot be undone.",
					size: "md",
					showHeader: true,
					showFooter: false,
					component: (
						<ConfirmWithPasswordModal
							onSubmit={async () => {
								await caseEvidenceMutation.mutateAsync(values);
							}}
						/>
					),
				});
			} catch (error) {
				console.log(error);
			}
		}
	};

	const handleAddSubmarkingSubmit = async (values: CaseEvidenceForm) => {
		const expirationTime = moment(storedTime).add(5, "minutes");
		if (storedTime && expirationTime !== undefined && moment().isBefore(expirationTime)) {
			await caseEvidenceSubmarkingMutation.mutateAsync(values);
		} else {
			try {
				modal.openModal({
					allowClose: true,
					title: "Confirm Submit",
					description: "Are you sure? This action cannot be undone.",
					size: "md",
					showHeader: true,
					showFooter: false,
					component: (
						<ConfirmWithPasswordModal
							onSubmit={async () => {
								await caseEvidenceSubmarkingMutation.mutateAsync(values);
							}}
						/>
					),
				});
			} catch (error) {
				console.log(error);
			}
		}
	};

	const handleUpdateMarking = (data?: CaseEvidence, submarking?: CaseEvidence) => {
		modal.openModal({
			title: "Evidence Marking Status",
			description: `Update evidence marking status of ${data?.exhibit || submarking?.exhibit}`,
			component: <CaseEvidenceUpdateMarkingStatusModal data={data || submarking} />,
		});
	};

	const handleUpdateAdmission = (data?: CaseEvidence, submarking?: CaseEvidence) => {
		modal.openModal({
			title: "Evidence Admission Status",
			description: `Update evidence admission status of ${data?.exhibit || submarking?.exhibit}`,
			component: <CaseEvidenceUpdateAdmissionStatusModal data={data || submarking} />,
		});
	};

	const handleEdit = (data?: CaseEvidence, submarking?: CaseEvidence) => {
		modal.openModal({
			title: "Edit Evidence",
			description: `Update Evidence Exhibit ${data?.exhibit || submarking?.exhibit}`,
			component: <CaseEvidenceEditModal data={data || submarking} onSubmit={handleSubmit} />,
		});
	};

	const handleAddSubmarking = (submarking?: CaseEvidence) => {
		modal.openModal({
			title: "Add Submarking",
			description: `Add Submarking Evidence of Exhibit ${submarking?.exhibit}`,
			component: <CaseEvidenceEditModal data={null} newSubmarking={submarking} onSubmit={handleAddSubmarkingSubmit} />,
		});
	};

	const handleAddSubmarkingOnDrawing = (submarking?: CaseEvidence) => {
		modal.openModal({
			title: "Add Submarking",
			description: `Add Submarking Evidence of ${submarking?.exhibit}`,
			component: <CaseEvidenceEditModal data={undefined} newSubmarking={submarking} onSubmit={handleBulkEvidence} />,
		});
	};
	const [showExhibitButtons, setShowExhibitButtons] = useState(!index || false);
	const [draftSubmarkings, setDraftSubmarkings] = useState<CaseEvidence[]>([]);
	const [closeDrawing, setCloseDrawing] = useState<boolean | undefined>();
	const [drawMode, setDrawMode] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [cropwMode, setCropMode] = useState<boolean>(false);
	const [newExhibit, setNewExhibit] = useState<string>("");

	const handleBulkEvidence = (values: CaseEvidence) => {
		const sub = Array.isArray(values) ? values : [values];
		setNewExhibit(values?.exhibit);
		if (draftSubmarkings.length === 0) {
			setDraftSubmarkings(sub);
		} else {
			draftSubmarkings.push(values);
		}
	};

	const handleUpdateDraftSubmarking = (values: CaseEvidence, index: number) => {
		modal.openModal({
			title: "Update Submarking",
			description: "Update Submarking Evidence",
			component: (
				<CaseEvidenceEditModal
					data={null}
					isDrawing={true}
					submarking={values}
					onSubmit={(updatedValue) => {
						setDraftSubmarkings((prev) => {
							const updated = [...prev];
							updated[index] = updatedValue;
							return updated;
						});
					}}
				/>
			),
		});
	};

	const handleSubmitDrawingUpdate = (img: Media) => {
		try {
			modal.openModal({
				allowClose: true,
				title: "Confirm Submit",
				description: "Are you sure? This action cannot be undone.",
				size: "md",
				showHeader: true,
				showFooter: false,
				component: (
					<ConfirmWithPasswordModal
						onSubmit={async () => {
							setIsLoading(true);
							try {
								await evidenceImageUpdateMutation.mutateAsync({
									id: data?.id,
									case_id: data?.case_id,
									case_record_user_id: data?.case_record_user_id,
									description: data?.description,
									parties: data?.parties,
									purpose: data?.purpose,
									evidence_type: data?.evidence_type,
									created_at: data?.created_at,
									evidence: img,
									attachment_deleted: false,
								});
								multipleSubmarkingMutation.mutateAsync(draftSubmarkings);
								setCloseDrawing(false);
								setDrawMode(false);
								setDraftSubmarkings([]);
								setNewExhibit("");
								setIsLoading(false);
							} catch (error) {
								console.log("Error:", error);
								setIsLoading(false);
							}
						}}
					/>
				),
			});
		} catch (error) {
			setIsLoading(false);
			console.log(error);
		}
	};
	const handleSubmitDrawingPdfUpdate = (pdf: Media) => {
		try {
			modal.openModal({
				allowClose: true,
				title: "Confirm Submit",
				description: "Are you sure? This action cannot be undone.",
				size: "md",
				showHeader: true,
				showFooter: false,
				component: (
					<ConfirmWithPasswordModal
						onSubmit={async () => {
							setIsLoading(true);
							try {
								pdf &&
									draftSubmarkings.length > 0 &&
									(await evidenceImageUpdateMutation.mutateAsync({
										id: data?.id,
										case_id: data?.case_id,
										case_record_user_id: data?.case_record_user_id,
										description: data?.description,
										parties: data?.parties,
										purpose: data?.purpose,
										evidence_type: data?.evidence_type,
										created_at: data?.created_at,
										evidence: pdf,
										attachment_deleted: false,
									}));
								pdf && draftSubmarkings.length > 0 && multipleSubmarkingMutation.mutateAsync(draftSubmarkings);
								setCloseDrawing(false);
								setDrawMode(false);
								setDraftSubmarkings([]);
								setNewExhibit("");
								setIsLoading(false);
							} catch (error) {
								console.log("Error:", error);
								setIsLoading(false);
							}
						}}
					/>
				),
			});
		} catch (error) {
			setIsLoading(false);
			console.log(error);
		}
	};
	const handleExpungeSubmarking = (sub: CaseEvidence) => {
		try {
			modal.openModal({
				allowClose: true,
				title: "Confirm Submit",
				description: "Are you sure? This action cannot be undone.",
				size: "md",
				showHeader: true,
				showFooter: false,
				component: (
					<ConfirmWithPasswordModal
						onSubmit={async () => {
							await expungeCaseMutation.mutateAsync({ ...sub, status: "Expunged" });
						}}
					/>
				),
			});
		} catch (error) {
			console.log(error);
		}
	};

	useEffect(() => {
		if (closeDrawing === false) {
			setCloseDrawing(undefined);
			setNewExhibit("");
		}
	}, [closeDrawing, setCloseDrawing]);
	const { closeModal } = useModalControl();
	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="w-full">
					<div className="flex flex-col w-full">
						<div className="flex flex-row items-center justify-between w-full h-[40px] px-4 bg-slate-50 border-b border-slate-200">
							<span className="text-sm font-bold text-foreground-dark">{attachment?.name || data?.evidence?.name}</span>
							<div className="inline-flex flex-row w-auto gap-3" />
						</div>
						<div className="flex grow shrink flex-row w-full h-full">
							<Tabs className="flex h-full" variant="compact" defaultValue="evidence-details">
								<TabsList className="pt-1">
									<TabsTrigger value="evidence-details">Pieces of Evidence</TabsTrigger>
								</TabsList>
								<TabsContent className="h-[calc(100svh-90px)]" value="evidence-details">
									<PanelGroup className="flex flex-row grow w-full" direction="horizontal">
										<Panel defaultSize={20} minSize={20} className="flex flex-col shrink-0 grow max-w-[420px]">
											<ScrollArea>
												<div className="flex flex-col">
													{/* exhibits buttons */}
													{showExhibitButtons ? (
														<div className="py-4 w-full">
															{data?.evidence_type !== "Testimonial"
																? activityData
																		?.filter((i) => i.id === caseActivityId)
																		.map((i) =>
																			i.exhibits.map((e) => (
																				<button
																					key={e.value}
																					onClick={() => {
																						setNewEvidenceId(e?.value);
																						setShowExhibitButtons(false);
																					}}
																					type="button"
																					className="group items-center w-full flex flex-row justify-between text-start px-3 py-2.5 gap-1 font-semibold text-base text-foreground/70 hover:bg-primary-50 hover:text-primary-600"
																				>
																					<span className="max-w-[310px] truncate">{e?.label}</span>
																					<IconArrowRight className="shrink-0 transition-opacity opacity-0 group-hover:opacity-100" size={14} stroke={2} />
																				</button>
																			)),
																		)
																: activityData
																		?.filter((i) => i.id === caseActivityId)
																		.map((i) =>
																			i.witness_name.map((e) => (
																				<button
																					key={e.value}
																					onClick={() => {
																						setNewEvidenceId(e?.value);
																						setShowExhibitButtons(false);
																					}}
																					type="button"
																					className="group flex flex-row items-center justify-between w-full relative  px-3 py-2.5 gap-1 font-semibold text-base text-foreground/70 hover:bg-primary-50 hover:text-foreground-dark "
																				>
																					{e?.label}
																					<IconArrowRight
																						className={clsx("shrink-0 transition-opacity opacity-0 group-hover:opacity-100")}
																						size={14}
																						stroke={2}
																					/>
																				</button>
																			)),
																		)}
														</div>
													) : (
														<div className="flex flex-col gap-3 p-4">
															{/* exhibits info w/ submarkings */}
															<button
																type="button"
																className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-foreground font-medium"
																onClick={() => setShowExhibitButtons(true)}
															>
																<IconArrowLeft size={18} stroke={2} />
																Back
															</button>
															<div className="flex flex-col gap-6">
																{/* main exhibit */}
																<div className="flex flex-col w-full gap-2 ">
																	{/* exhibit */}
																	<div className="flex flex-col w-full gap-2">
																		{data?.evidence_type !== "Testimonial" && (
																			<div className="flex flex-col w-full">
																				<div className="group flex flex-col w-full  items-start shrink-0 border-b border-border/50 last:border-b-0">
																					{/* status */}
																					{data?.status && (
																						<div className="col-span-12">
																							<Tooltip
																								className="z-[90]"
																								side="right"
																								content={
																									<div className="flex flex-col w-full gap-1">
																										<div className="text-foreground-dark/90">Expunged Date</div>
																										<div className="text-foreground-dark/70 font-medium">{data?.expunged_at}</div>
																									</div>
																								}
																							>
																								<div className="inline-flex items-center h-[18px] px-2 rounded-full bg-red-600/10 text-red-600">
																									<span className="text-xs font-semibold">Expunged</span>
																								</div>
																							</Tooltip>
																						</div>
																					)}
																					<div className=" flex flex-row w-full items-center gap-2 shrink-0">
																						<span className="text-base font-semibold text-foreground-dark leading-tight">Exhibit</span>
																						{data?.status !== "Expunged" && (
																							<Tooltip content="Update" side="right" className="z-[999]">
																								<button
																									className="opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-600"
																									type="button"
																									onClick={() => {
																										form.setValue("id", data?.id);
																										handleEdit(data, null);
																									}}
																								>
																									<IconPencil size={16} stroke={2} />
																								</button>
																							</Tooltip>
																						)}
																					</div>
																					<span className="text-base text-foreground/70 leading-tight">{data?.exhibit}</span>
																				</div>
																			</div>
																		)}
																		{/* witness name */}
																		{data?.witness_name && (
																			<div className="col-span-12 w-full">
																				<div className="flex flex-col w-full shrink-0 border-b border-border/50 last:border-b-0">
																					{/* status */}
																					{data?.status && (
																						<div className="col-span-12">
																							<Tooltip
																								className="z-[90]"
																								side="right"
																								content={
																									<div className="flex flex-col w-full gap-1">
																										<div className="text-foreground-dark/90">Expunged Date</div>
																										<div className="text-foreground-dark/70 font-medium">{data?.expunged_at}</div>
																									</div>
																								}
																							>
																								<div className="inline-flex items-center h-[18px] px-2 rounded-full bg-red-600/10 text-red-600">
																									<span className="text-xs font-semibold">Expunged</span>
																								</div>
																							</Tooltip>
																						</div>
																					)}
																					<div className=" flex flex-row w-full items-center gap-2 shrink-0">
																						<span className="text-base font-semibold text-foreground-dark leading-tight">Witness Name</span>
																						{data?.status !== "Expunged" && (
																							<Tooltip content="Update" side="right" className="z-[999]">
																								<button
																									className="opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-600"
																									type="button"
																									onClick={() => {
																										form.setValue("id", data?.id);
																										handleEdit(data, null);
																									}}
																								>
																									<IconPencil size={16} stroke={2} />
																								</button>
																							</Tooltip>
																						)}
																					</div>
																					<span className="text-base text-foreground/70 leading-tight">{data?.witness_name}</span>
																				</div>
																			</div>
																		)}
																		{/* description */}
																		<div className="flex flex-col max-w-[320px]">
																			<div className="flex flex-col shrink-0 border-b border-border/50 last:border-b-0">
																				<div className="text-base font-semibold text-foreground-dark leading-tight">Description</div>
																				<div className="text-base text-foreground/70 leading-tight break-words">{data?.description}</div>
																			</div>
																		</div>
																		{/* purpose */}
																		{data?.purpose && (
																			<div className="col-span-12 w-full">
																				<div className="flex flex-col w-full shrink-0 border-b border-border/50 last:border-b-0">
																					<span className="text-base font-semibold text-foreground-dark leading-tight">Purpose</span>
																					<span className="text-base text-foreground/70 leading-tight">{data?.purpose}</span>
																				</div>
																			</div>
																		)}
																	</div>
																	{data?.evidence_type !== "Testimonial" && (
																		<>
																			{/* marking status */}
																			<div className="group flex flex-col w-full">
																				<div className="flex flex-col w-full items-start gap-2 shrink-0 border-b border-border/50 last:border-b-0">
																					<div className="flex flex-row w-full items-center gap-2 shrink-0">
																						<span className="text-base font-semibold text-foreground-dark leading-tight">Marking Status</span>
																						{data?.status !== "Expunged" && (
																							<Tooltip content="Update" side="right" className="z-[999]">
																								<button
																									className="opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-600"
																									type="button"
																									onClick={() => {
																										form.setValue("id", data?.id);
																										handleUpdateMarking(data, null);
																									}}
																								>
																									<IconPencil size={16} stroke={2} />
																								</button>
																							</Tooltip>
																						)}
																					</div>
																					{data?.marking_status && (
																						<Tooltip
																							className="z-[90]"
																							content={
																								<div className="col-span-12 w-full">
																									<div className="flex flex-col w-full shrink-0 border-b border-border/50 last:border-b-0">
																										<span className="text-base font-semibold text-foreground-dark leading-tight">
																											Marking Status Update Date
																										</span>
																										<span className="text-base text-foreground/70 leading-tight">{data?.updated_marking_status_at}</span>
																									</div>
																								</div>
																							}
																							side="right"
																						>
																							<div className="inline-flex items-center h-[18px] px-2 rounded-full bg-green-600/10 text-green-600">
																								<span className="text-xs font-semibold">{data?.marking_status}</span>
																							</div>
																						</Tooltip>
																					)}
																				</div>
																			</div>
																			{/* admission status */}
																			<div className="flex flex-col w-full">
																				<div className="group flex flex-col w-full items-start gap-2 shrink-0 border-b border-border/50 last:border-b-0">
																					<div className="flex flex-row w-full items-center gap-2 shrink-0">
																						<span className="text-base font-semibold text-foreground-dark leading-tight">Admission Status</span>
																						{data?.status !== "Expunged" && (
																							<Tooltip content="Update" side="right" className="z-[999]">
																								<button
																									className="opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-600"
																									type="button"
																									onClick={() => {
																										form.setValue("id", data?.id);
																										handleUpdateAdmission(data, null);
																									}}
																								>
																									<IconPencil size={16} stroke={2} />
																								</button>
																							</Tooltip>
																						)}
																					</div>
																					{data?.admission_status && (
																						<Tooltip
																							className="z-[90]"
																							content={
																								<div className="col-span-12 w-full">
																									<div className="flex flex-col w-full shrink-0 border-b border-border/50 last:border-b-0">
																										<span className="text-base font-semibold text-foreground-dark leading-tight">
																											Admission Status Update Date
																										</span>
																										<span className="text-base text-foreground/70 leading-tight">{data?.updated_admission_status_at}</span>
																									</div>
																								</div>
																							}
																							side="right"
																						>
																							<div
																								className={clsx("inline-flex items-center h-[18px] px-2 rounded-full", {
																									"bg-green-600/10 text-green-600": data?.admission_status === "Admitted",
																									"bg-red-600/10 text-red-600": data?.admission_status === "Denied",
																									"bg-orange-600/10 text-orange-600": data?.admission_status === "Denied but Tendered",
																								})}
																							>
																								<span className="text-xs font-semibold">{data?.admission_status}</span>
																							</div>
																						</Tooltip>
																					)}
																					{/* category */}
																					{data?.category && (
																						<div className="col-span-12 w-full">
																							<span className="text-base font-semibold text-foreground-dark leading-tight">Category</span>
																							<div className="flex flex-row flex-wrap w-full py-1 gap-3">
																								{data?.category.map((value) => (
																									<div
																										key={value.value}
																										className="inline-flex shrink-0 justify-center items-center min-w-[18px] h-[18px] px-2 rounded-full bg-slate-200 text-xs font-bold text-foreground"
																									>
																										{value.value}
																									</div>
																								))}
																							</div>
																						</div>
																					)}
																				</div>
																			</div>
																		</>
																	)}
																</div>
																{/* draft submarkings */}
																{draftSubmarkings?.length > 0 && (
																	<div className="flex flex-col w-full divide-y divide-border/50">
																		<div className="flex flex-col w-full">
																			<div className="sticky top-0 flex flex-row items-center justify-between w-full py-2 border-t border-b border-border/50 bg-background">
																				<span className="text-base text-foreground font-semibold flex gap-2 items-center">
																					Draft Submarkings
																					<span className="inline-flex justify-center items-center w-[18px] h-[18px] shrink-0 p-1 bg-primary-100/70 text-primary-600 rounded-full text-sm font-semibold">
																						{draftSubmarkings?.length}
																					</span>
																				</span>
																			</div>
																			{draftSubmarkings?.length !== 0 || draftSubmarkings !== null ? (
																				<div className="flex flex-col w-full divide-y divide-dashed divide-border">
																					{draftSubmarkings.map((sub, index) => (
																						<div key={index} className="flex flex-row w-full py-4 gap-3">
																							<div className="inline-flex shrink-0 justify-center items-center min-w-[18px] h-[18px] px-1 rounded-full bg-slate-200 text-xs font-bold text-foreground">
																								{index + 1}
																							</div>
																							<div className="flex flex-col w-full gap-2">
																								<div className="flex flex-col w-full">
																									<div className="group flex flex-col w-full items-start shrink-0 border-b border-border/50 last:border-b-0">
																										<div className="flex flex-row w-full items-center gap-2 shrink-0">
																											<span className="text-base font-semibold text-foreground-dark leading-tight">Exhibit</span>
																											{data?.status !== "Expunged" && (
																												<>
																													<Tooltip content="Update" side="top" className="z-[999]">
																														<button
																															className="opacity-100 text-blue-500 hover:text-blue-600"
																															type="button"
																															onClick={() => {
																																handleUpdateDraftSubmarking(sub, index);
																															}}
																														>
																															<IconPencil size={16} stroke={2} />
																														</button>
																													</Tooltip>
																													<Tooltip content="Remove" side="top" className="z-[999]">
																														<button
																															className="opacity-100 text-red-500 hover:text-red-600"
																															type="button"
																															onClick={() => {
																																draftSubmarkings.splice(index, 1);
																																setDraftSubmarkings((prev) => [...prev]);
																															}}
																														>
																															<IconTrash size={16} stroke={2} />
																														</button>
																													</Tooltip>
																												</>
																											)}
																										</div>
																										<span className="text-base text-foreground/70 leading-tight">{sub.exhibit}</span>
																									</div>
																								</div>
																								{/* description */}
																								<div className="col-span-12 w-full">
																									<div className="flex flex-col w-full shrink-0 border-b border-border/50 last:border-b-0">
																										<span className="text-base font-semibold text-foreground-dark leading-tight">Description</span>
																										<span className="text-base text-foreground/70 leading-tight">{sub.description}</span>
																									</div>
																								</div>
																								{/* purpose */}
																								{sub.purpose && (
																									<div className="col-span-12 w-full">
																										<div className="flex flex-col w-full shrink-0 border-b border-border/50 last:border-b-0">
																											<span className="text-base font-semibold text-foreground-dark leading-tight">Purpose</span>
																											<span className="text-base text-foreground/70 leading-tight">{sub.purpose}</span>
																										</div>
																									</div>
																								)}
																							</div>
																						</div>
																					))}
																				</div>
																			) : (
																				<Blanket width="sm">
																					<BlanketImage icon={<IconDatabaseExclamation size={24} stroke={1.5} />} />
																					<BlanketInfo
																						title="No Submarkings"
																						message="No available data for now. Please check back later or refresh the page."
																					/>
																				</Blanket>
																			)}
																		</div>
																	</div>
																)}
																{/* submarkings */}
																{data?.evidence_type !== "Testimonial" && (
																	<div className="flex flex-col w-full divide-y divide-border/50">
																		<div className="flex flex-col w-full">
																			<div className="sticky top-0 flex flex-row items-center justify-between w-full py-2 border-t border-b border-border/50 bg-background">
																				<span className="text-base text-foreground font-semibold flex gap-2 items-center">
																					Submarkings
																					<span className="inline-flex justify-center items-center w-[18px] h-[18px] shrink-0 p-1 bg-primary-100/70 text-primary-600 rounded-full text-sm font-semibold">
																						{submarkings?.length}
																					</span>
																				</span>

																				{data?.status !== "Expunged" && !drawMode && (
																					<div className="flex flex-row w-full justify-end gap-2">
																						<DropdownMenu modal={false}>
																							<DropdownMenuTrigger asChild>
																								<Button className="" size="sm" variant="green" type="button">
																									<span className="">Add</span>
																									<IconChevronDown size={16} stroke={2.5} />
																								</Button>
																							</DropdownMenuTrigger>
																							<DropdownMenuContent>
																								<DropdownMenuItem
																									onClick={() => {
																										form.setValue("parent_id", data?.id);
																										!drawMode ? handleAddSubmarking(data, null) : handleAddSubmarkingOnDrawing(data);
																									}}
																								>
																									<div className="flex flex-row items-center gap-2 px-2 py-1">
																										<span>Submarking</span>
																									</div>
																								</DropdownMenuItem>
																								{["image/jpeg", "image/png", "png", "jpg", "jpeg"].includes(
																									attachment?.mime_type || data?.evidence?.mime_type,
																								) && (
																									<DropdownMenuItem
																										onClick={() => {
																											setDrawMode(true);
																										}}
																									>
																										<div className="flex flex-row items-center gap-2 px-2 py-1">
																											<span>Submarking (Draw)</span>
																										</div>
																									</DropdownMenuItem>
																								)}
																								{["application/pdf", "application/octet-stream"].includes(
																									attachment?.mime_type || data?.evidence.mime_type,
																								) && (
																									<DropdownMenuItem
																										onClick={() => {
																											setDrawMode(true);
																										}}
																									>
																										<div className="flex flex-row items-center gap-2 px-2 py-1">
																											<span>Submarking (Draw)</span>
																										</div>
																									</DropdownMenuItem>
																								)}
																							</DropdownMenuContent>
																						</DropdownMenu>
																					</div>
																				)}
																			</div>

																			{submarkings?.length !== 0 ? (
																				<div className="flex flex-col w-full divide-y divide-dashed divide-border">
																					{submarkings?.map((sub, index) => (
																						<div key={sub.id} className="flex flex-row w-full py-4 gap-3">
																							<div className="inline-flex shrink-0 justify-center items-center min-w-[18px] h-[18px] px-1 rounded-full bg-slate-200 text-xs font-bold text-foreground">
																								{index + 1}
																							</div>
																							<div className="flex flex-col w-full gap-2">
																								<div className="flex flex-col w-full">
																									<div className="group flex flex-col w-full items-start shrink-0 border-b border-border/50 last:border-b-0">
																										<div className="flex flex-row w-full items-center gap-2 shrink-0">
																											<div className="flex flex-col w-full">
																												<span className="text-base text-foreground font-semibold flex gap-2 items-center">
																													{`Exhibit ${sub.exhibit}`}
																												</span>
																											</div>
																											{data?.status !== "Expunged" && (
																												<div className="flex flex-row w-full justify-end gap-2">
																													<DropdownMenu modal={false}>
																														<DropdownMenuTrigger asChild>
																															<Button className="shrink-0 !rounded-full !w-[24px] !h-[24px]" size="icon" variant="ghost">
																																<IconDots size={16} stroke={1.5} />
																															</Button>
																														</DropdownMenuTrigger>
																														<DropdownMenuContent>
																															{sub.evidence && (
																																<DropdownMenuItem
																																	onClick={() => {
																																		handleClick(sub);
																																	}}
																																>
																																	<div className="flex flex-row items-center gap-2 px-2 py-1">
																																		<span>View</span>
																																	</div>
																																</DropdownMenuItem>
																															)}
																															<DropdownMenuItem
																																onClick={() => {
																																	form.setValue("id", sub.id);
																																	handleEdit(null, sub);
																																}}
																															>
																																<div className="flex flex-row items-center gap-2 px-2 py-1">
																																	<span>Update</span>
																																</div>
																															</DropdownMenuItem>
																															<DropdownMenuItem
																																onClick={() => {
																																	handleUpdateMarking(null, sub);
																																	form.setValue("id", sub.id);
																																}}
																															>
																																<div className="flex flex-row items-center gap-2 px-2 py-1">
																																	<span>Update Marking Status</span>
																																</div>
																															</DropdownMenuItem>
																															<DropdownMenuItem
																																onClick={() => {
																																	handleUpdateAdmission(null, sub);
																																	form.setValue("id", sub.id);
																																}}
																															>
																																<div className="flex flex-row items-center gap-2 px-2 py-1">
																																	<span>Update Admission Status</span>
																																</div>
																															</DropdownMenuItem>
																															<DropdownMenuSeparator />
																															<DropdownMenuItem
																																onClick={() => {
																																	form.setValue("id", sub.id);
																																	handleExpungeSubmarking(sub);
																																}}
																																dangerZone
																															>
																																<div className="flex flex-row items-center gap-2 px-2 py-1">
																																	<span>Expunge</span>
																																</div>
																															</DropdownMenuItem>
																														</DropdownMenuContent>
																													</DropdownMenu>
																												</div>
																											)}
																										</div>
																										{sub?.created_at && (
																											<span className="text-base text-xs text-foreground/70 leading-tight">{sub.created_at}</span>
																										)}
																									</div>
																								</div>
																								{/* description */}
																								<div className="col-span-12 w-full">
																									<div className="flex flex-col w-full shrink-0 border-b border-border/50 last:border-b-0">
																										<span className="text-base font-semibold text-foreground-dark leading-tight">Description</span>
																										<span className="text-base text-foreground/70 leading-tight">{sub.description}</span>
																									</div>
																								</div>
																								{/* purpose */}
																								{sub.purpose && (
																									<div className="col-span-12 w-full">
																										<div className="flex flex-col w-full shrink-0 border-b border-border/50 last:border-b-0">
																											<span className="text-base font-semibold text-foreground-dark leading-tight">Purpose</span>
																											<span className="text-base text-foreground/70 leading-tight">{sub.purpose}</span>
																										</div>
																									</div>
																								)}
																								{/* witness name */}
																								{sub.witness_name && (
																									<div className="col-span-12 w-full">
																										<div className="flex flex-col w-full shrink-0 border-b border-border/50 last:border-b-0">
																											<span className="text-base font-semibold text-foreground-dark leading-tight">Witness Name</span>
																											<span className="text-base text-foreground/70 leading-tight">{sub.witness_name}</span>
																										</div>
																									</div>
																								)}

																								{/* marking status */}
																								<div className="flex flex-col w-full">
																									<div className="group flex flex-col w-full items-start gap-2 shrink-0 border-b border-border/50 last:border-b-0">
																										<div className="flex flex-row w-full items-center gap-2 shrink-0">
																											<span className="text-base font-semibold text-foreground-dark leading-tight">Marking Status</span>
																										</div>
																										{sub.marking_status && (
																											<Tooltip
																												className="z-[90]"
																												content={
																													<div className="col-span-12 w-full">
																														<div className="flex flex-col w-full shrink-0 border-b border-border/50 last:border-b-0">
																															<span className="font-semibold text-foreground-dark leading-tight">
																																Marking Status Update Date
																															</span>
																															<span className="text-foreground/70 leading-tight">{sub.updated_marking_status_at}</span>
																														</div>
																													</div>
																												}
																												side="right"
																											>
																												<div className="inline-flex items-center h-[20px] px-2 rounded bg-green-600/10 text-green-600">
																													<span className="text-xs font-semibold">{sub.marking_status}</span>
																												</div>
																											</Tooltip>
																										)}
																									</div>
																								</div>

																								{/* admission status */}
																								<div className="flex flex-col w-full">
																									<div className="group flex flex-col w-full items-start gap-2 shrink-0 border-b border-border/50 last:border-b-0">
																										<div className="flex flex-row w-full items-center gap-2 shrink-0">
																											<span className="text-base font-semibold text-foreground-dark leading-tight">Admission Status</span>
																										</div>
																										{sub.admission_status && (
																											<Tooltip
																												className="z-[90]"
																												content={
																													<div className="col-span-12 w-full">
																														<div className="flex flex-col w-full shrink-0 border-b border-border/50 last:border-b-0">
																															<span className="font-semibold text-foreground-dark leading-tight">
																																Admission Status Update Date
																															</span>
																															<span className="text-foreground/70 leading-tight">{sub.updated_admission_status_at}</span>
																														</div>
																													</div>
																												}
																												side="right"
																											>
																												<div
																													className={clsx("inline-flex items-center h-[20px] px-2 rounded", {
																														"bg-green-600/10 text-green-600": sub.admission_status === "Admitted",
																														"bg-red-600/10 text-red-600": sub.admission_status === "Denied",
																														"bg-orange-600/10 text-orange-600": sub?.admission_status === "Denied but Tendered",
																													})}
																												>
																													<span className="text-xs font-semibold">{sub.admission_status}</span>
																												</div>
																											</Tooltip>
																										)}
																									</div>
																								</div>
																								{/* category */}
																								{sub?.category && (
																									<div className="col-span-12 w-full">
																										<span className="text-base font-semibold text-foreground-dark leading-tight">Category</span>
																										<div className="flex flex-row flex-wrap w-full py-1 gap-3">
																											{sub?.category.map((value) => (
																												<div
																													key={value.value}
																													className="inline-flex shrink-0 justify-center items-center min-w-[18px] h-[18px] px-2 rounded-full bg-slate-200 text-xs font-bold text-foreground"
																												>
																													{value.value}
																												</div>
																											))}
																										</div>
																									</div>
																								)}
																							</div>
																						</div>
																					))}
																				</div>
																			) : (
																				<Blanket width="sm">
																					<BlanketImage icon={<IconDatabaseExclamation size={24} stroke={1.5} />} />
																					<BlanketInfo
																						title="No Submarkings"
																						message="No available data for now. Please check back later or refresh the page."
																					/>
																				</Blanket>
																			)}
																		</div>
																	</div>
																)}
															</div>
														</div>
													)}
												</div>
											</ScrollArea>
											<div className="flex flex-row items-center w-full gap-2 mt-auto border-t border-border/50 p-3">
												<Button type="button" onClick={closeModal} className="w-full" variant="ghost">
													Close
												</Button>
											</div>
										</Panel>

										<PanelResizeHandle className="group flex justify-center items-center w-[12px] border-r border-l border-border/50 bg-white">
											<div className="inline-flex w-[4px] h-[40px] rounded-sm bg-foreground/50 group-hover:bg-foreground/90" />
										</PanelResizeHandle>

										<Panel defaultSize={80} minSize={80} maxSize={80} className="flex flex-col shrink grow w-full h-full bg-slate-100 !pt-0">
											{showExhibitButtons ? (
												<Blanket>
													<BlanketImage icon={<IconTemplate size={24} stroke={1.5} />} />
													<BlanketInfo title="Select an Exhibit" message="Click an exhibit to view it on this panel." />
												</Blanket>
											) : data?.evidence ? (
												<div>
													{["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(
														attachment?.mime_type || data?.evidence.mime_type,
													) && (
														<DocumentEditor
															id="onlyoffice-editor"
															documentServerUrl={import.meta.env.VITE_DOC_SERVER_URL} // Update with your document server URL
															config={config}
															width="100%"
															height="100%"
														/>
													)}

													{["application/pdf", "application/octet-stream"].includes(attachment?.mime_type || data?.evidence.mime_type) && (
														<EvidencePdfViewer
															evidence={data}
															attachment={attachment || data?.evidence}
															file={attachment?.original_url || data?.evidence?.original_url}
															isDrawMode={drawMode}
															newExhibit={newExhibit}
															isLoading={isLoading}
															handleSetPdf={handleSubmitDrawingPdfUpdate}
															handleEvidence={handleBulkEvidence}
															handleDrawMode={(value: boolean) => setDrawMode(value)}
															handleCancelled={(value) => {
																if (value === true) {
																	setDraftSubmarkings([]);
																	setNewExhibit("");
																}
															}}
															closeDrawing={closeDrawing}
														/>
													)}

													{["image/jpeg", "image/png", "png", "jpg", "jpeg"].includes(attachment?.mime_type || data?.evidence.mime_type) && (
														// <img src={attachment?.original_url || data?.evidence.original_url} alt={attachment?.name || data?.evidence.name} />
														<ImageViewerWithAddEvidence
															evidenceData={data}
															src={attachment?.original_url || data?.evidence.original_url}
															alt={attachment?.name || data?.evidence.name}
															handleSetImage={handleSubmitDrawingUpdate}
															isLoading={isLoading}
															isDrawMode={drawMode}
															isCropMode={cropwMode}
															closeDrawing={closeDrawing}
															handleDrawMode={(value: boolean) => setDrawMode(value)}
															newExhibit={newExhibit}
															handleEvidence={handleBulkEvidence}
															handleCancelled={(value) => {
																if (value === true) {
																	setDraftSubmarkings([]);
																}
															}}
														/>
													)}
													{["video/mp4"].includes(attachment?.mime_type || data?.evidence.mime_type) && (
														// biome-ignore lint/a11y/useMediaCaption: <no>
														<video controls width="100%">
															<source src={attachment?.original_url || data?.evidence.original_url} type="video/mp4" />
															Your browser does not support the video tag.
														</video>
													)}
												</div>
											) : (
												<Blanket>
													<BlanketImage icon={<IconFile size={24} stroke={1.5} />} />
													<BlanketInfo title="No judicial affidavit found." message="Please check back later or refresh the page." />
												</Blanket>
											)}
										</Panel>
									</PanelGroup>
								</TabsContent>
							</Tabs>
						</div>
					</div>
				</div>
			</form>
		</Form>
	);
}
